import React, { useCallback, useEffect, useMemo, useState } from "react";
import { 
  Search, 
  Download, 
  User, 
  Mail, 
  Phone
} from "lucide-react";
import { adminApiRequest } from "../utils/adminApi";

export default function Customers() {
  const [data, setData] = useState({
    studio: [],
    wedding: [],
    rental: [],
    shop: [],
    print: [],
    completed: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  const loadAllData = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const [studio, wedding, rental, shop, print, completed] = await Promise.all([
        adminApiRequest({ script: "list_studio_bookings" }).catch(() => ({ success: false })),
        adminApiRequest({ script: "list_wedding_package_bookings" }).catch(() => ({ success: false })),
        adminApiRequest({ script: "list_studio_rental_bookings" }).catch(() => ({ success: false })),
        adminApiRequest({ script: "fetch_shop_orders" }).catch(() => ({ success: false })),
        adminApiRequest({ script: "fetch_print_orders" }).catch(() => ({ success: false })),
        adminApiRequest({ script: "fetch_all_completed_orders" }).catch(() => ({ success: false }))
      ]);

      setData({
        studio: studio?.success ? (Array.isArray(studio.data) ? studio.data : []) : [],
        wedding: wedding?.success ? (Array.isArray(wedding.data) ? wedding.data : []) : [],
        rental: rental?.success ? (Array.isArray(rental.data) ? rental.data : []) : [],
        shop: shop?.success ? (Array.isArray(shop.orders) ? shop.orders : []) : [],
        print: print?.success ? (Array.isArray(print.orders) ? print.orders : []) : [],
        completed: completed?.success ? (Array.isArray(completed.orders) ? completed.orders : []) : []
      });
    } catch (err) {
      setError("Failed to load customer data from one or more sources.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAllData();
  }, [loadAllData]);

  const customers = useMemo(() => {
    const customerMap = new Map();

    const addCustomer = (name, email, phone, type, date) => {
      if (!email) return;
      const key = email.toLowerCase().trim();
      
      if (!customerMap.has(key)) {
        customerMap.set(key, {
          name: name || "Unknown",
          email: key,
          phone: phone || "",
          bookings: [],
          lastActivity: date
        });
      }

      const customer = customerMap.get(key);
      if (!customer.phone && phone) customer.phone = phone;
      if (customer.name === "Unknown" && name) customer.name = name;
      
      customer.bookings.push({ type, date });
      if (new Date(date) > new Date(customer.lastActivity)) {
        customer.lastActivity = date;
      }
    };

    data.studio?.forEach(b => addCustomer(b.customer_name, b.customer_email, b.customer_phone, "Studio", b.created_at));
    data.wedding?.forEach(b => addCustomer(b.full_name, b.email, b.phone, "Wedding", b.created_at));
    data.rental?.forEach(b => addCustomer(b.full_name, b.email, b.phone, "Rental", b.created_at));
    data.shop?.forEach(o => addCustomer(o.customer_name, o.customer_email, o.customer_phone, "Shop", o.created_at));
    data.print?.forEach(o => addCustomer(o.full_name, o.email, o.phone, "Print", o.created_at));
    data.completed?.forEach(o => {
      const type = o.type === 'shop' ? 'Shop' : (o.type === 'print' ? 'Print' : 'Order');
      addCustomer(o.customer_name, o.customer_email, o.customer_phone, type, o.created_at);
    });

    return Array.from(customerMap.values()).sort((a, b) => new Date(b.lastActivity) - new Date(a.lastActivity));
  }, [data]);

  const filteredCustomers = useMemo(() => {
    if (!searchTerm) return customers;
    const term = searchTerm.toLowerCase();
    return customers.filter(c => 
      c.name.toLowerCase().includes(term) || 
      c.email.toLowerCase().includes(term) || 
      c.phone.includes(term)
    );
  }, [customers, searchTerm]);



  const exportToCSV = () => {
    const headers = ["Name", "Email", "Phone", "Services Used"];
    const rows = filteredCustomers.map(c => [
      c.name,
      c.email,
      c.phone || "Not provided",
      Array.from(new Set(c.bookings.map(b => b.type))).join(" | ")
    ]);

    const csvContent = [headers, ...rows].map(e => e.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `customers_export_${new Date().toISOString().slice(0, 10)}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <section className="h-full min-h-0">
      <div className="flex h-full min-h-0 flex-col rounded-3xl border border-slate-200 bg-white shadow-sm">
        {/* Header */}
        <div className="border-b border-slate-200 p-5">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-2xl font-semibold text-slate-900">Customers</h2>
              <p className="mt-1 text-sm text-slate-500">
                Manage your customer database aggregated from all services.
              </p>
            </div>
            
            <button 
              onClick={exportToCSV}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-800"
            >
              <Download className="h-4 w-4" />
              <span>Export CSV</span>
            </button>
          </div>

          <div className="mt-6 relative">
            <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input 
              type="text"
              placeholder="Search by name, email or phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full rounded-2xl border border-slate-200 bg-slate-50 py-2.5 pl-10 pr-4 text-sm text-slate-900 outline-none transition focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10"
            />
          </div>
        </div>

        {/* Content */}
        <div className="min-h-0 flex-1 overflow-auto p-5">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20">
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-200 border-t-indigo-600"></div>
              <p className="mt-4 text-sm text-slate-500 font-medium">Aggregating customer data...</p>
            </div>
          ) : error ? (
            <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-center">
              <p className="text-sm text-red-600 font-medium">{error}</p>
              <button 
                onClick={loadAllData}
                className="mt-4 text-sm font-semibold text-red-700 underline"
              >
                Try again
              </button>
            </div>
          ) : filteredCustomers.length === 0 ? (
            <div className="rounded-3xl border border-dashed border-slate-200 bg-slate-50/50 py-20 text-center">
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-white shadow-sm ring-1 ring-slate-200">
                <User className="h-6 w-6 text-slate-400" />
              </div>
              <h3 className="text-sm font-semibold text-slate-900">No customers found</h3>
              <p className="mt-1 text-xs text-slate-500">
                {searchTerm ? "Try adjusting your search terms." : "Bookings will appear here once they are made."}
              </p>
            </div>
          ) : (
            <div className="overflow-hidden rounded-2xl border border-slate-200">
              <div className="overflow-x-auto">
                <table className="min-w-full text-left">
                  <thead className="bg-slate-50/80 backdrop-blur-sm sticky top-0 z-10">
                    <tr className="text-xs font-bold uppercase tracking-wider text-slate-500">
                      <th className="px-6 py-4">Customer Name</th>
                      <th className="px-6 py-4">Email Address</th>
                      <th className="px-6 py-4">Phone</th>
                      <th className="px-6 py-4 text-center">Services Used</th>
                      <th className="px-6 py-4 text-right">Actions</th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-slate-100 bg-white">
                    {filteredCustomers.map((customer) => (
                      <tr key={customer.email} className="group hover:bg-slate-50/50 transition-colors">
                        <td className="px-6 py-5">
                          <div className="text-sm font-bold text-slate-900 truncate">
                            {customer.name}
                          </div>
                        </td>

                        <td className="px-6 py-5">
                          <div className="text-sm font-medium text-slate-600">
                            {customer.email}
                          </div>
                        </td>

                        <td className="px-6 py-5">
                          {customer.phone ? (
                            <div className="text-sm font-medium text-slate-700">
                              {customer.phone}
                            </div>
                          ) : (
                            <span className="text-xs text-slate-400 italic">Not provided</span>
                          )}
                        </td>

                        <td className="px-6 py-5">
                          <div className="flex flex-wrap justify-center gap-1 text-[11px] font-bold uppercase tracking-tight text-slate-500">
                            {Array.from(new Set(customer.bookings.map(b => b.type))).join(", ")}
                          </div>
                        </td>

                        <td className="px-6 py-5 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <a 
                              href={`mailto:${customer.email}`}
                              className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 shadow-sm transition hover:border-indigo-200 hover:bg-indigo-50 hover:text-indigo-600"
                            >
                              <Mail className="h-4 w-4" />
                            </a>
                            {customer.phone && (
                              <a 
                                href={`tel:${customer.phone}`}
                                className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 shadow-sm transition hover:border-indigo-200 hover:bg-indigo-50 hover:text-indigo-600"
                              >
                                <Phone className="h-4 w-4" />
                              </a>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
